"use client";

import { CalendarDays, Video, Trophy } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "[01] Diagnostic",
  description: "Nous identifions la source exacte du blocage ou du problème structurel grâce à nos équipements de pointe.",
  cta: "Nos services",
  stickerColor: "bg-primary",
  Icon: CalendarDays,
  offsetClass: "md:mt-0"
}, {
  number: "02",
  title: "[02] Intervention",
  description: "Débouchage haute pression ou inspection par caméra pour une résolution rapide et sans dégâts.",
  cta: "Voir nos experts",
  stickerColor: "bg-secondary",
  Icon: Video,
  offsetClass: "md:mt-6"
}, {
  number: "03",
  title: "[03] Certification",
  description: "Validation du débit optimal et rapport détaillé pour votre tranquillité d'esprit. Satisfaction garantie.",
  cta: "Zones desservies",
  stickerColor: "bg-muted",
  Icon: Trophy,
  offsetClass: "md:mt-0"
}];
export default function NotreProcessus() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: pathRef,
    isVisible: pathVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1313" id="notre-processus" className="relative bg-background py-24 md:py-32 overflow-hidden text-foreground">
      {}
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <div ref={headRef} className="max-w-[720px] mx-auto text-center transition-all duration-700 ease-out" style={{
        opacity: headVisible ? 1 : 0,
        transform: headVisible ? "translateY(0)" : "translateY(16px)",
        transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
      }}>
          {}
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="inline-block w-1.5 h-1.5 rounded-md bg-primary" />
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">
              § PROTOCOLE · 03 / NOTRE MÉTHODE
            </span>
            <span className="inline-block w-1.5 h-1.5 rounded-md bg-primary" />
          </div>

          {}
          <h2 className="font-default font-semibold text-foreground text-[clamp(2rem,4.4vw,3.75rem)] leading-[1.1] tracking-tighter" style={{
          fontFamily: "var(--font-serif)"
        }}>
            Votre solution en{" "}
            <span className="relative inline-block italic">
              3 étapes
              <svg aria-hidden="true" className="absolute -inset-x-3 -inset-y-2 w-[calc(100%+1.5rem)] h-[calc(100%+1rem)] pointer-events-none" viewBox="0 0 200 80" preserveAspectRatio="none" fill="none">
                <path d="M 20 40 C 15 18, 60 8, 100 10 C 150 12, 188 22, 192 42 C 195 60, 155 72, 100 70 C 45 68, 8 60, 14 40 Z" stroke="hsl(217 91% 60%)" strokeWidth="2" strokeLinecap="round" strokeDasharray="600" strokeDashoffset={headVisible ? 0 : 600} style={{
                transition: "stroke-dashoffset 800ms ease-out 400ms",
                transform: "rotate(-2deg)",
                transformOrigin: "center"
              }} />
              </svg>
            </span>{" "}
            précises.
          </h2>

          <p className="mt-6 mx-auto max-w-[56ch] text-[clamp(1.0625rem,1.25vw,1.1875rem)] leading-[1.6] text-muted-foreground font-default">
            Du premier appel à la vérification finale par caméra — voici comment OT Solution assure la fluidité de vos drains avec une rigueur chirurgicale.
          </p>
        </div>

        {}
        <div ref={cardsRef} className="relative mt-20 md:mt-24">
          {}
          <div ref={pathRef} className="hidden md:block absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none z-0" aria-hidden="true">
            <svg viewBox="0 0 1200 200" className="w-full h-[200px]" preserveAspectRatio="none" fill="none">
              <path d="M 80 100 Q 250 30, 420 100 T 780 100 Q 950 170, 1120 100" stroke="hsl(217 91% 60%)" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="8 6" pathLength={1} style={{
              strokeDasharray: pathVisible ? "8 6" : "0 1000",
              transition: "stroke-dasharray 1500ms ease-out",
              opacity: 0.3
            }} />
            </svg>
          </div>

          {}
          <div className="md:hidden absolute left-1/2 -translate-x-1/2 top-0 bottom-0 pointer-events-none z-0" aria-hidden="true">
            <svg viewBox="0 0 40 800" className="h-full w-10" preserveAspectRatio="none" fill="none">
              <path d="M 20 20 Q 30 200, 20 400 T 20 780" stroke="hsl(217 91% 60%)" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="8 6" style={{
              opacity: 0.3
            }} />
            </svg>
          </div>

          <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {steps.map((step, i) => {
            const Icon = step.Icon;
            return <div key={i} data-index={i} className={`group relative ${step.offsetClass} transition-all duration-700 ease-out`} style={{
              opacity: cardsVisible ? 1 : 0,
              transform: cardsVisible ? "translateY(0)" : "translateY(16px)",
              transitionDelay: `${i * 120}ms`,
              transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
            }}>
                  {}
                  <div aria-hidden="true" className="absolute -top-10 right-6 opacity-0 group-hover:opacity-100 translate-y-3 group-hover:-translate-y-0 transition-all duration-300 ease-out pointer-events-none z-20">
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                      <line x1="28" y1="14" x2="28" y2="6" stroke="hsl(217 91% 60%)" strokeWidth="2" strokeLinecap="round" />
                      <circle cx="28" cy="5" r="3" fill="hsl(38 92% 50%)" stroke="hsl(217 91% 60%)" strokeWidth="2" />
                      <rect x="10" y="14" width="36" height="30" rx="4" fill="hsl(214 32% 91%)" stroke="hsl(217 91% 60%)" strokeWidth="2" />
                      <circle cx="21" cy="28" r="2.5" fill="hsl(217 91% 60%)" />
                      <circle cx="35" cy="28" r="2.5" fill="hsl(217 91% 60%)" />
                      <path d="M 24 36 Q 28 39, 32 36" stroke="hsl(217 91% 60%)" strokeWidth="2" strokeLinecap="round" fill="none" />
                    </svg>
                  </div>

                  <Card className="relative bg-card border border-border rounded-md p-7 pt-8 transition-all duration-300 ease-out group-hover:-translate-y-1 overflow-hidden shadow-md" style={{
                borderLeft: "4px solid hsl(217 91% 60%)"
              }} onMouseEnter={e => {
                e.currentTarget.style.boxShadow = "0 8px 20px -4px rgba(0,0,0,0.1)";
              }} onMouseLeave={e => {
                e.currentTarget.style.boxShadow = "0 4px 12px 0px rgba(0,0,0,0.08)";
              }}>
                    {}
                    <div aria-hidden="true" className="absolute top-2 left-4 font-default font-semibold text-primary/[0.08] pointer-events-none select-none leading-none" style={{
                  fontFamily: "var(--font-serif)",
                  fontSize: "4.5rem"
                }}>
                      {step.number}
                    </div>

                    <CardContent className="relative px-0">
                      {}
                      <div className="relative inline-flex items-center justify-center mb-6">
                        <div className={`relative w-16 h-16 ${step.stickerColor} rounded-md border border-border flex items-center justify-center shadow-sm`}>
                          <Icon className="w-7 h-7 text-primary-foreground" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                        </div>
                      </div>

                      <h3 className="font-default font-semibold text-foreground text-[clamp(1.375rem,2.2vw,1.875rem)] leading-[1.2] tracking-tight mb-3">
                        {step.title}
                      </h3>

                      <p className="text-base leading-[1.65] text-muted-foreground mb-5 font-default">
                        {step.description}
                      </p>

                      {}
                      <button type="button" className="group/link relative inline-flex items-center text-primary font-medium text-sm tracking-tight font-default">
                        <span className="relative">
                          {step.cta}
                          <span aria-hidden="true" className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-primary transition-all duration-300 ease-out group-hover/link:w-full" />
                        </span>
                        <span className="ml-1.5 transition-transform duration-200 group-hover/link:translate-x-1">→</span>
                      </button>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}